// src/features/agent-builder/components/ToolList.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { PlatformToolData, CustomToolData } from '@dopomogai/types';
import { PlatformToolItem } from './PlatformToolItem';
import { CustomToolItem } from './CustomToolItem';
import clsx from 'clsx';

interface ToolListProps {
  tools: (PlatformToolData | CustomToolData)[];
  toolType: 'platform' | 'custom';
  onToggleTool: (toolId: string, isEnabled: boolean) => void; // REQUIRED
  onEditTool?: (toolId: string) => void;
  onDeleteTool?: (toolId: string) => void;
  className?: string;
  emptyListMessageKey?: string;
}

export const ToolList: React.FC<ToolListProps> = ({
  tools,
  toolType,
  onToggleTool, // This is now a non-optional function
  onEditTool,
  onDeleteTool,
  className,
  emptyListMessageKey = "agentBuilder:step3.customSkills.emptyList"
}) => {
  const { t } = useTranslation('agentBuilder');

  if (!tools || tools.length === 0) {
    return (
      <div className={clsx(
        "py-8 text-center text-md text-slate-500 italic",
        "border-2 border-black border-dashed rounded-xl bg-slate-50",
        "min-h-[100px] flex items-center justify-center",
        className
      )}>
        {t(emptyListMessageKey)}
      </div>
    );
  }

  return (
    <div className={clsx("space-y-3", className)}>
      {tools.map((tool) => {
        // Since onToggleTool is required for ToolList, we can always pass it.
        // The child components PlatformToolItem and CustomToolItem also require onToggle.

        if (toolType === 'platform' && 'nameKey' in tool && 'descriptionKey' in tool) {
          // This type guard confirms `tool` is PlatformToolData
          return (
            <PlatformToolItem
              key={tool.id}
              tool={tool} // No need to cast if type guard is sufficient
              onToggle={onToggleTool} // Pass directly
            />
          );
        } else if (toolType === 'custom' && 'name' in tool) {
          // This type guard confirms `tool` is CustomToolData
          // Ensure CustomToolData has isEnabled and isMutable defined in your types package!
          
          // For onEditTool and onDeleteTool, they are optional for ToolList.
          // CustomToolItem expects them to be defined.
          // So, we MUST provide them or ensure CustomToolItem can handle them being undefined.
          // Assuming CustomToolItem REQUIRES onEdit and onDelete:
          if (!onEditTool || !onDeleteTool) {
            // This case should ideally not happen if toolType is 'custom' based on how you structure usage.
            // If it can, CustomToolItem's props for onEdit/onDelete should be optional.
            console.error("ToolList: onEditTool or onDeleteTool is missing for custom tool type but required by CustomToolItem.");
            return ( // Render a fallback or minimal version if essential handlers are missing
                <div key={tool.id} className="p-4 border-2 border-red-500 rounded-xl bg-red-50 text-red-700">
                    Error: Configuration mismatch for custom tool "{tool.name}". Edit/Delete handlers missing.
                </div>
            );
          }
          return (
            <CustomToolItem
              key={tool.id}
              tool={tool} // No need to cast if type guard is sufficient
              onToggle={onToggleTool} // Pass directly
              onEdit={onEditTool}     // Pass directly (after the check above)
              onDelete={onDeleteTool}   // Pass directly (after the check above)
            />
          );
        }
        // Fallback for unrecognized tool structure
        console.warn("ToolList: Tool object did not match expected type for rendering.", tool);
        return (
            <div key={tool.id || Math.random()} className="p-4 border-2 border-yellow-500 rounded-xl bg-yellow-50 text-yellow-700">
                Warning: Unrecognized tool structure for tool ID: {tool.id || 'Unknown'}.
            </div>
        );
      })}
    </div>
  );
};